import { DEFAULT_BINDINGS, MOUSE_ACTIONS, type Action } from '../config/controls';

/** Largest mouse delta consumed in one frame, pixels. */
const MAX_LOOK_DELTA = 320;
/** Gamepad stick values below this are treated as centred. */
const GAMEPAD_DEAD_ZONE = 0.18;
/** Look pixels produced by a fully deflected right stick in one frame. */
const GAMEPAD_LOOK_SCALE = 14;

interface GamepadInput {
  moveX: number;
  moveY: number;
  lookX: number;
  lookY: number;
  jumpPressed: boolean;
  crouchHeld: boolean;
  sneakHeld: boolean;
  grabHeld: boolean;
  firePressed: boolean;
  grapplePressed: boolean;
  meleePressed: boolean;
  switchWeaponPressed: boolean;
  toggleCameraPressed: boolean;
  pausePressed: boolean;
  restartPressed: boolean;
}

const EMPTY_GAMEPAD: GamepadInput = {
  moveX: 0,
  moveY: 0,
  lookX: 0,
  lookY: 0,
  jumpPressed: false,
  crouchHeld: false,
  sneakHeld: false,
  grabHeld: false,
  firePressed: false,
  grapplePressed: false,
  meleePressed: false,
  switchWeaponPressed: false,
  toggleCameraPressed: false,
  pausePressed: false,
  restartPressed: false,
};

function applyDeadZone(value: number): number {
  const magnitude = Math.abs(value);
  if (magnitude < GAMEPAD_DEAD_ZONE) return 0;
  const scaled = (magnitude - GAMEPAD_DEAD_ZONE) / (1 - GAMEPAD_DEAD_ZONE);
  return Math.sign(value) * scaled;
}

export interface InputFrame {
  /** Strafe (-1 left .. 1 right) and forward (-1 back .. 1 forward). */
  move: { x: number; y: number };
  /** Accumulated mouse movement in pixels since the last frame. */
  look: { x: number; y: number };
  jumpPressed: boolean;
  crouchHeld: boolean;
  sneakHeld: boolean;
  grabHeld: boolean;
  firePressed: boolean;
  grapplePressed: boolean;
  meleePressed: boolean;
  switchWeaponPressed: boolean;
  toggleCameraPressed: boolean;
  toggleHeadTrackingPressed: boolean;
  /** Settle the view onto the surface the body just stepped onto. */
  swivelViewPressed: boolean;
  /** The map builder's scanner: palette, orientation and removal. */
  buildNextPressed: boolean;
  buildVariantPressed: boolean;
  buildRotatePressed: boolean;
  buildDeletePressed: boolean;
  buildMagneticPressed: boolean;
  buildDronePressed: boolean;
  buildWaypointPressed: boolean;
  pausePressed: boolean;
  restartPressed: boolean;
  debugPressed: boolean;
}

/**
 * Translates raw keyboard/mouse events into logical actions.
 * The class never reads the DOM state directly during a frame: everything is
 * accumulated by listeners and consumed by `sample()`.
 */
export class Input {
  private readonly held = new Set<Action>();
  private readonly pressed = new Set<Action>();
  private lookX = 0;
  private lookY = 0;
  /** The virtual joystick's analog move axis, -1..1. */
  private touchMoveX = 0;
  private touchMoveY = 0;
  private pointerLocked = false;
  private enabled = true;
  /** Notified whenever the browser gains or loses the pointer lock. */
  onPointerLockChange: ((locked: boolean) => void) | null = null;
  private previousGamepadButtons: boolean[] = [];
  private padConnected = false;
  private disposers: Array<() => void> = [];

  constructor(private readonly bindings: Record<Action, string[]> = DEFAULT_BINDINGS) {}

  /** Attach DOM listeners. Safe to call once; `dispose` removes them again. */
  attach(target: Window = window, documentRef: Document = document): void {
    // A key maps to a list of actions: one physical key can legitimately drive
    // more than one logical action (R is both restart, outside the builder, and
    // the scanner's rotate while building), and the game decides which applies
    // from the phase it is in.
    const keyMap = new Map<string, Action[]>();
    const mouseMap = new Map<number, Action>();
    for (const [action, codes] of Object.entries(this.bindings) as [Action, string[]][]) {
      for (const code of codes) {
        // Mouse bindings use the pseudo code 'Mouse0' for the left button.
        const button = /^Mouse(\d)$/.exec(code);
        if (button) {
          mouseMap.set(Number(button[1]), action);
          continue;
        }
        const actions = keyMap.get(code);
        if (actions) actions.push(action);
        else keyMap.set(code, [action]);
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      const actions = keyMap.get(event.code);
      if (!actions) return;
      if (actions.includes('pause') || actions.includes('debug')) event.preventDefault();
      if (event.repeat) return;
      for (const action of actions) {
        this.held.add(action);
        this.pressed.add(action);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      const actions = keyMap.get(event.code);
      if (actions) for (const action of actions) this.held.delete(action);
    };

    const onMouseDown = (event: MouseEvent) => {
      const action = mouseMap.get(event.button);
      if (!action) return;
      // A click while the pointer is free is the gesture that recaptures the
      // mouse; it must not also fire the weapon.
      if (!this.pointerLocked) return;
      this.held.add(action);
      this.pressed.add(action);
    };

    const onMouseUp = (event: MouseEvent) => {
      const action = mouseMap.get(event.button);
      if (action) this.held.delete(action);
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!this.pointerLocked) return;
      this.lookX += event.movementX;
      this.lookY += event.movementY;
    };

    const onContextMenu = (event: MouseEvent) => event.preventDefault();

    const onBlur = () => this.held.clear();

    const onPointerLockChange = () => {
      this.pointerLocked = documentRef.pointerLockElement !== null;
      if (!this.pointerLocked) this.held.clear();
      this.onPointerLockChange?.(this.pointerLocked);
    };

    target.addEventListener('keydown', onKeyDown);
    target.addEventListener('keyup', onKeyUp);
    target.addEventListener('mousedown', onMouseDown);
    target.addEventListener('mouseup', onMouseUp);
    target.addEventListener('mousemove', onMouseMove);
    target.addEventListener('blur', onBlur);
    documentRef.addEventListener('contextmenu', onContextMenu);
    documentRef.addEventListener('pointerlockchange', onPointerLockChange);

    this.disposers.push(
      () => target.removeEventListener('keydown', onKeyDown),
      () => target.removeEventListener('keyup', onKeyUp),
      () => target.removeEventListener('mousedown', onMouseDown),
      () => target.removeEventListener('mouseup', onMouseUp),
      () => target.removeEventListener('mousemove', onMouseMove),
      () => target.removeEventListener('blur', onBlur),
      () => documentRef.removeEventListener('contextmenu', onContextMenu),
      () => documentRef.removeEventListener('pointerlockchange', onPointerLockChange),
    );
  }

  dispose(): void {
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.held.clear();
      this.touchMoveX = 0;
      this.touchMoveY = 0;
    }
  }

  get isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  get hasGamepad(): boolean {
    return this.padConnected;
  }

  /**
   * Ask the browser to capture the mouse. Returns false when the request is
   * unavailable or rejected, so the caller can show a recapture prompt.
   */
  async requestPointerLock(element: HTMLElement): Promise<boolean> {
    if (typeof element.requestPointerLock !== 'function') return false;
    try {
      const pending = element.requestPointerLock();
      if (pending && typeof pending.then === 'function') await pending;
      // Browsers use null when unlocked; jsdom leaves it undefined, so test
      // the value's truthiness instead of comparing to null.
      return Boolean(document.pointerLockElement);
    } catch {
      return false;
    }
  }

  exitPointerLock(): void {
    if (typeof document !== 'undefined' && document.pointerLockElement) document.exitPointerLock();
  }

  /** Consume the accumulated input for this frame. */
  sample(): InputFrame {
    const pad = this.pollGamepad();
    const lookX = Math.max(-MAX_LOOK_DELTA, Math.min(MAX_LOOK_DELTA, this.lookX));
    const lookY = Math.max(-MAX_LOOK_DELTA, Math.min(MAX_LOOK_DELTA, this.lookY));

    const frame: InputFrame = {
      move: { x: 0, y: 0 },
      look: {
        x: this.enabled ? lookX + pad.lookX : 0,
        y: this.enabled ? lookY + pad.lookY : 0,
      },
      jumpPressed: (this.enabled && this.pressed.has('jump')) || pad.jumpPressed,
      crouchHeld: (this.enabled && this.held.has('crouch')) || pad.crouchHeld,
      sneakHeld: (this.enabled && this.held.has('sneak')) || pad.sneakHeld,
      grabHeld: (this.enabled && this.held.has('grab')) || pad.grabHeld,
      firePressed: (this.enabled && this.pressed.has('fire')) || pad.firePressed,
      grapplePressed: (this.enabled && this.pressed.has('grapple')) || pad.grapplePressed,
      meleePressed: (this.enabled && this.pressed.has('melee')) || pad.meleePressed,
      switchWeaponPressed:
        (this.enabled && this.pressed.has('switchWeapon')) || pad.switchWeaponPressed,
      // Interface actions stay live while the simulation is paused, otherwise
      // the player could not un-pause with Escape.
      toggleCameraPressed: this.pressed.has('toggleCamera') || pad.toggleCameraPressed,
      toggleHeadTrackingPressed: this.pressed.has('toggleHeadTracking'),
      swivelViewPressed: this.enabled && this.pressed.has('swivelView'),
      buildNextPressed: this.enabled && this.pressed.has('buildNext'),
      buildVariantPressed: this.enabled && this.pressed.has('buildVariant'),
      buildRotatePressed: this.enabled && this.pressed.has('buildRotate'),
      buildDeletePressed: this.enabled && this.pressed.has('buildDelete'),
      buildMagneticPressed: this.enabled && this.pressed.has('buildMagnetic'),
      buildDronePressed: this.enabled && this.pressed.has('buildDrone'),
      buildWaypointPressed: this.enabled && this.pressed.has('buildWaypoint'),
      pausePressed: this.pressed.has('pause') || pad.pausePressed,
      restartPressed: this.pressed.has('restart') || pad.restartPressed,
      debugPressed: this.pressed.has('debug'),
    };

    if (this.enabled) {
      const axis = (action: Action): number => (this.held.has(action) ? 1 : 0);
      frame.move.x = Math.max(
        -1,
        Math.min(1, axis('moveRight') - axis('moveLeft') + pad.moveX + this.touchMoveX),
      );
      frame.move.y = Math.max(
        -1,
        Math.min(1, axis('moveForward') - axis('moveBack') + pad.moveY + this.touchMoveY),
      );
    }

    this.lookX = 0;
    this.lookY = 0;
    this.pressed.clear();
    return frame;
  }

  /**
   * Read the first connected standard-mapping gamepad. Button edges are
   * derived from the previous poll so a held button fires once.
   */
  private pollGamepad(): GamepadInput {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') {
      this.previousGamepadButtons = [];
      this.padConnected = false;
      return EMPTY_GAMEPAD;
    }

    let pad: Gamepad | null = null;
    try {
      for (const candidate of navigator.getGamepads()) {
        if (candidate && candidate.connected) {
          pad = candidate;
          break;
        }
      }
    } catch {
      pad = null;
    }

    if (!pad) {
      this.previousGamepadButtons = [];
      this.padConnected = false;
      return EMPTY_GAMEPAD;
    }
    this.padConnected = true;

    const buttons = pad.buttons.map((button) => button.pressed || button.value > 0.5);
    const edge = (index: number): boolean =>
      (buttons[index] ?? false) && !(this.previousGamepadButtons[index] ?? false);

    const result: GamepadInput = {
      moveX: applyDeadZone(pad.axes[0] ?? 0),
      moveY: -applyDeadZone(pad.axes[1] ?? 0),
      lookX: applyDeadZone(pad.axes[2] ?? 0) * GAMEPAD_LOOK_SCALE,
      lookY: applyDeadZone(pad.axes[3] ?? 0) * GAMEPAD_LOOK_SCALE,
      jumpPressed: edge(0),
      crouchHeld: buttons[1] ?? false,
      sneakHeld: buttons[10] ?? false,
      grabHeld: buttons[2] ?? false,
      firePressed: edge(7) || edge(5),
      grapplePressed: edge(1),
      meleePressed: edge(4),
      switchWeaponPressed: edge(2),
      toggleCameraPressed: edge(3),
      pausePressed: edge(9),
      restartPressed: edge(8),
    };

    this.previousGamepadButtons = buttons;
    return result;
  }

  /** Inject an action programmatically; used by tests and by menu buttons. */
  inject(action: Action, pressed = true): void {
    if (pressed) {
      this.held.add(action);
      this.pressed.add(action);
    } else {
      this.held.delete(action);
    }
  }

  /**
   * Set the virtual joystick's move axis, -1..1.
   *
   * It is merged with the keys and the gamepad stick in `sample()`, so a touch
   * player gets the same analog movement the pad path already has.
   */
  setTouchMove(x: number, y: number): void {
    this.touchMoveX = Math.max(-1, Math.min(1, x));
    this.touchMoveY = Math.max(-1, Math.min(1, y));
  }

  /**
   * Accumulate look movement from a touch drag, in mouse pixels.
   *
   * Touch look bypasses the pointer lock: a drag on the canvas is already a
   * user gesture, and mobile browsers do not offer a lock worth waiting for.
   */
  addTouchLook(deltaX: number, deltaY: number): void {
    if (!this.enabled) return;
    this.lookX += deltaX;
    this.lookY += deltaY;
  }

  /** Mouse actions are pointer-lock driven; exposed for completeness. */
  static get mouseActions(): ReadonlySet<Action> {
    return MOUSE_ACTIONS;
  }
}
